/**
 * VaultWorkspacePage — the Vault's file workspace, full width, in three views
 * over the same files:
 *
 *   Desktop — a canvas of icons and folder windows (arrangement saved per
 *             person, on the server);
 *   Browse  — the folder tree and list/grid (uploads, All files, Trash);
 *   Search  — free metadata filters with Standard / By Type / By Arche.
 *
 * Rendered by My Data (`/my-data`) and by the Vault Documents arche (`/`).
 * The route wrappers own the workspace choice (their headers host the
 * HolderSwitcher); this page owns the view, the storage strip and panel, and
 * the view's URL.
 *
 * Which view opens: `lib/workspace-view.ts` — an explicit `?view=` wins, a
 * `?folderPath=` deep link forces Browse (a saved preference never swallows
 * a deep link), then the saved choice, then Desktop. Once the person switches
 * view, `?view=` and the saved choice follow, and `folderPath` leaves the URL
 * so a refresh doesn't jump back — the folder is handed to the next view once
 * instead (the Desktop opens its window).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FolderTree, Monitor, Search as SearchIcon } from 'lucide-react';
import type { HolderSwitcherOption } from '@/components/HolderSwitcher';
import { cn } from '@/lib/utils';
import {
  readSavedWorkspaceView,
  resolveWorkspaceView,
  saveWorkspaceView,
  type WorkspaceView,
} from '../lib/workspace-view';
import { WorkspaceStoragePanel, WorkspaceStorageStrip } from '../components/WorkspaceStorage';
import { DocumentsListPage } from './DocumentsListPage';
import { VaultSearchView } from './VaultSearchView';
import { DesktopView, type DesktopHandle } from '../components/desktop/DesktopView';
import { useToolbarRoom } from '../hooks/useToolbarRoom';
import { useOpenFile } from '../hooks/useOpenFile';
import { useVaultDocumentFolders } from '../hooks/useVaultDocuments';
import { useWorkspaceAI } from '../hooks/useWorkspaceAI';
import { useDesktopStyle } from '../lib/desktop-style';
import { api } from '@/lib/api';

interface VaultWorkspacePageProps {
  holderId: string;
  holder: HolderSwitcherOption | null | undefined;
  /** Extra toolbar controls — the workspace switcher, while the page is
   *  expanded (focus mode) and its own header, which normally carries the
   *  switcher, is hidden. */
  toolbarExtra?: ReactNode;
  /** Controls at the toolbar's far end — the focus-mode way back (Fias,
   *  Worktop, Fias AI, Collapse) while expanded. A render function: when the
   *  row runs short of room they drop their text labels (`useToolbarRoom`). */
  toolbarEnd?: (room: { showLabels: boolean }) => ReactNode;
}

const VIEW_TABS: Array<{ view: WorkspaceView; label: string; icon: typeof Monitor }> = [
  { view: 'desktop', label: 'Desktop', icon: Monitor },
  { view: 'browse', label: 'Browse', icon: FolderTree },
  { view: 'search', label: 'Search', icon: SearchIcon },
];

export function VaultWorkspacePage({
  holderId,
  holder,
  toolbarExtra,
  toolbarEnd,
}: VaultWorkspacePageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  // A folder link names a folder in the workspace it was opened in. After a
  // workspace switch the link (still in the URL) no longer applies — never
  // reveal a same-path folder in another workspace.
  const linkedPath = searchParams.get('folderPath');
  const link = useRef({ path: linkedPath, holder: holderId });
  if (link.current.path !== linkedPath) link.current = { path: linkedPath, holder: holderId };
  const folderPath = linkedPath && link.current.holder === holderId ? linkedPath : null;
  const [saved] = useState(readSavedWorkspaceView);
  const view = useMemo(
    () => resolveWorkspaceView({ viewParam, folderPath, saved }),
    [viewParam, folderPath, saved],
  );
  /** A deep-linked folder, carried into the next view once it leaves the URL. */
  const [revealFolderPath, setRevealFolderPath] = useState<string | null>(folderPath);
  useEffect(() => {
    if (folderPath) setRevealFolderPath(folderPath);
  }, [folderPath]);
  const [storageOpen, setStorageOpen] = useState(false);
  // A deep link names a folder in THIS workspace: switching workspace drops
  // it rather than opening a same-path folder somewhere else.
  const shownHolder = useRef(holderId);
  useEffect(() => {
    if (shownHolder.current === holderId) return;
    shownHolder.current = holderId;
    setRevealFolderPath(null);
  }, [holderId]);

  const switchView = useCallback(
    (next: WorkspaceView) => {
      saveWorkspaceView(next);
      // Only the Desktop takes a deep-linked folder along (it opens the
      // folder's window); anywhere else, the person has moved on.
      if (next !== 'desktop') setRevealFolderPath(null);
      const params = new URLSearchParams(searchParams);
      params.set('view', next);
      params.delete('folderPath');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // One line while there's room: short of it, the storage figures go first
  // (the toolbar keeps the icon — the panel is still one click away — and
  // Browse, which has the room, shows the figures instead), then the
  // focus-mode controls' labels.
  const rowRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const roomRefs = useMemo(
    () => ({ row: rowRef, fixed: tabsRef, end: endRef, strip: stripRef, controls: controlsRef }),
    [],
  );
  const room = useToolbarRoom(roomRefs);
  const figuresFit = room === 0;
  const openStorage = useCallback(() => setStorageOpen(true), []);

  // --- Fias AI: what it can do on screen, and see of it (ids only) ---------
  const folders = useVaultDocumentFolders(holderId);
  const [desktopStyle] = useDesktopStyle();
  const opener = useOpenFile();
  const desktop = useRef<DesktopHandle | null>(null);
  /** Actions that need the Desktop, waiting for it to mount. */
  const waitingForDesktop = useRef<Array<(handle: DesktopHandle) => void>>([]);
  const setDesktopHandle = useCallback((handle: DesktopHandle | null) => {
    desktop.current = handle;
    if (!handle) return;
    const waiting = waitingForDesktop.current;
    waitingForDesktop.current = [];
    for (const run of waiting) run(handle);
  }, []);
  const withDesktop = (run: (handle: DesktopHandle) => void | Promise<void>) => {
    if (view === 'desktop' && desktop.current) return run(desktop.current);
    waitingForDesktop.current.push((handle) => void run(handle));
    if (view !== 'desktop') switchView('desktop');
  };
  /** Browse's folder, as it reports it; and a way to send it somewhere. */
  const browseLocation = useRef<string | null>(null);
  const rememberBrowseLocation = useCallback((location: string) => {
    browseLocation.current = location;
  }, []);
  const [browseTarget, setBrowseTarget] = useState<{ path: string | null; nonce: number }>({
    path: null,
    nonce: 0,
  });
  const browseTo = (folderId: string) => {
    const path =
      folderId === 'root'
        ? null
        : (folders.data?.find((f) => f.folderId === folderId)?.path ?? null);
    if (folderId !== 'root' && !path) return;
    setBrowseTarget((prev) => ({ path, nonce: prev.nonce + 1 }));
    if (view !== 'browse') switchView('browse');
  };
  const fetchOwnDocument = async (documentId: string) => {
    const { document } = await api.getVaultDocument(documentId);
    if (document.holderId !== holderId) throw new Error('Not in this workspace');
    return document;
  };
  useWorkspaceAI({
    // The assistant acts in personal and org workspaces; a guild space is
    // refused server-side, so it isn't offered there.
    enabled: holder?.holderType !== 'guild',
    getState: () => {
      const d = view === 'desktop' ? desktop.current?.snapshot() : undefined;
      return {
        view,
        holderId,
        openFolderId:
          d?.openFolderId ??
          (view === 'browse' && browseLocation.current && browseLocation.current !== 'all'
            ? browseLocation.current
            : null),
        openFolderIds: d?.openFolderIds ?? [],
        selectedDocumentIds: d?.selectedDocumentIds ?? [],
        selectedFolderIds: d?.selectedFolderIds ?? [],
        viewingDocumentId: d?.viewingDocumentId ?? opener.viewerDoc?.documentId ?? null,
        desktopStyle,
      };
    },
    openFolder: (folderId, newWindow) => {
      if (view === 'desktop') return withDesktop((h) => h.openFolder(folderId, { newWindow }));
      browseTo(folderId);
    },
    openFile: async (documentId) => {
      if (view === 'desktop') return withDesktop((h) => h.openFile(documentId));
      opener.open(await fetchOwnDocument(documentId));
    },
    reveal: async (ids) => {
      if (view === 'desktop') return withDesktop((h) => h.reveal(ids));
      // Browse shows the folder the first of them is in.
      const first = ids.documentIds[0];
      if (first) {
        const doc = await fetchOwnDocument(first);
        browseTo(doc.folderId ?? 'root');
      } else if (ids.folderIds[0]) {
        const folder = folders.data?.find((f) => f.folderId === ids.folderIds[0]);
        browseTo(folder?.parentFolderId ?? 'root');
      }
    },
    switchView,
    setFolderView: (folderId, patch) => withDesktop((h) => h.setFolderView(folderId, patch)),
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={rowRef}
        className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card/40 px-4 py-2"
      >
        <div
          ref={tabsRef}
          role="tablist"
          aria-label="Workspace view"
          className="flex items-center rounded-md border border-border bg-card p-0.5 text-sm"
        >
          {VIEW_TABS.map(({ view: tab, label, icon: Icon }) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={view === tab}
              onClick={() => view !== tab && switchView(tab)}
              className={cn(
                'flex items-center gap-1.5 rounded px-3 py-1.5 transition-colors',
                view === tab
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="h-4 w-4" aria-hidden />
              {label}
            </button>
          ))}
        </div>
        <div ref={endRef} className="flex min-w-0 flex-wrap items-center gap-2">
          {toolbarExtra}
          <div ref={stripRef} className="flex">
            <WorkspaceStorageStrip holderId={holderId} onOpen={openStorage} compact={!figuresFit} />
          </div>
          {toolbarEnd && (
            <div ref={controlsRef} className="flex">
              {toolbarEnd({ showLabels: room < 2 })}
            </div>
          )}
        </div>
      </div>

      {view === 'desktop' && (
        <DesktopView
          key={holderId}
          handleRef={setDesktopHandle}
          holderId={holderId}
          holder={holder}
          revealFolderPath={revealFolderPath}
          onRevealed={() => setRevealFolderPath(null)}
        />
      )}
      {view === 'browse' && (
        <DocumentsListPage
          key={`${holderId}:${browseTarget.nonce}`}
          holderId={holderId}
          holder={holder}
          initialFolderPath={
            (browseTarget.nonce > 0 ? browseTarget.path : null) ??
            folderPath ??
            revealFolderPath ??
            undefined
          }
          onLocationChange={rememberBrowseLocation}
          embedded
          storageSlot={
            figuresFit ? undefined : (
              <WorkspaceStorageStrip holderId={holderId} onOpen={openStorage} />
            )
          }
        />
      )}
      {view === 'search' && <VaultSearchView key={holderId} holderId={holderId} holder={holder} />}

      <WorkspaceStoragePanel holderId={holderId} open={storageOpen} onOpenChange={setStorageOpen} />
      {opener.ui}
    </div>
  );
}
